/**
 * Idioma de la interfaz.
 *
 * Per device, not per user and not synced. The language belongs to whoever is holding the
 * phone — the owner reviewing books in English and the person at the counter selling in Spanish
 * are looking at the same data through different words, and neither should change the other's
 * screen.
 *
 * Kept in localStorage rather than in Dexie so the very first render already knows: the install
 * screen and the PIN pad both appear before any database read finishes, and a flash of the
 * wrong language is exactly the kind of cheapness this app should not have.
 *
 * Spanish is the default and the source. This is a Nicaraguan shop; English is the exception,
 * for the half of the household that has to read the books.
 */

export const IDIOMAS = ['es', 'en'] as const;
export type Idioma = (typeof IDIOMAS)[number];

const CLAVE = 'abastece.idioma';

function leer(): Idioma {
  try {
    const v = localStorage.getItem(CLAVE);
    return v === 'en' || v === 'es' ? v : 'es';
  } catch {
    // Private windows and locked-down browsers throw on access. Spanish, then.
    return 'es';
  }
}

let actual: Idioma = leer();
const oyentes = new Set<() => void>();

/**
 * The BCP 47 tag for dates and numbers.
 *
 * `en-GB` rather than `en-US` on purpose: Nicaragua writes the day before the month, and a
 * settlement dated 09/10 meaning the ninth of October must not read as the tenth of September
 * to the person checking the books.
 */
const LOCALES: Record<Idioma, string> = { es: 'es-NI', en: 'en-GB' };

export function localeActual(): string {
  return LOCALES[actual];
}

function aplicarLang(): void {
  if (typeof document !== 'undefined') document.documentElement.lang = LOCALES[actual];
}

aplicarLang();

export function idiomaActual(): Idioma {
  return actual;
}

export function cambiarIdioma(idioma: Idioma): void {
  if (idioma === actual) return;
  actual = idioma;
  try {
    localStorage.setItem(CLAVE, idioma);
  } catch {
    /* The choice still applies to this session; it just will not survive a reload. */
  }
  aplicarLang();
  for (const fn of oyentes) fn();
}

export function suscribirIdioma(fn: () => void): () => void {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

export const ETIQUETA_IDIOMA: Record<Idioma, string> = {
  es: 'Español',
  en: 'English',
};

/**
 * For a header, where "Español" next to "English" would push the shop's own name off screen.
 *
 * Two letters is a weaker signal than the word, so it is only used where the word does not
 * fit and where somebody has already been through a screen that spelled it out.
 */
export const ETIQUETA_IDIOMA_BREVE: Record<Idioma, string> = {
  es: 'ES',
  en: 'EN',
};
